import { motion, type Variants, AnimatePresence } from 'framer-motion';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { Dialog, DialogButton, DialogDescription, DialogRoot, DialogTitle } from '~/components/ui/Dialog';
import { db, projectId, deleteByProjectId, getAll, description } from '~/lib/persistence';
import { clearProjectRules } from '~/lib/stores/rules';
import { cubicEasingFn } from '~/utils/easings';
import { json, type LoaderFunction } from '@remix-run/cloudflare';
import { logger } from '~/utils/logger';
import { HistoryItem } from './HistoryItem';
import { binDates } from './date-binning';
import { X, Search, PanelLeftClose, SquarePen, PanelLeftOpen, ChevronRight } from 'lucide-react';
import { useStore } from '@nanostores/react';
import { userStore } from '~/lib/stores/user';
import * as Sentry from '@sentry/remix';
import { planNameCleaner } from '~/lib/utils/planNameCleaner';
import { useDebounce } from '~/hooks/useDebounce';
import { useTranslation } from '~/lib/hooks/useTranslation';
import type { ChatHistoryItem } from '~/services/interfaceService';
import { Loading } from '~/components/chat/Loading';
import { isMobile } from '~/utils/mobile';
import { chatStore } from '@/lib/stores/chat';
import { ProUpgradeModal } from '~/components/ui/ProUpgradeModal';
import { shortcutEventEmitter } from '~/lib/hooks/useShortcuts';
import { shortcutsStore } from '~/lib/stores/settings';
import { isMac } from '@/utils/platform';
import { isDev } from '@/utils/constants';

export const loader: LoaderFunction = async ({ context }) => {
  const baseUrl =
    process.env.NEXT_PUBLIC_GET_ACCESS_URL_DEV ||
    process.env.NEXT_PUBLIC_GET_ACCESS_URL ||
    context.cloudflare.env.NEXT_PUBLIC_GET_ACCESS_URL;

  return json(baseUrl);
};

const menuVariants = {
  closed: {
    opacity: 0,
    visibility: 'hidden',
    left: '-150px',
    transition: {
      duration: 0.2,
      ease: cubicEasingFn,
    },
  },
  open: {
    opacity: 1,
    visibility: 'initial',
    left: 0,
    transition: {
      duration: 0.2,
      ease: cubicEasingFn,
    },
  },
} satisfies Variants;

const categoryVariants = {
  closed: {
    height: 0,
    opacity: 0,
    marginTop: 0,
    transition: {
      duration: 0.3,
      ease: cubicEasingFn,
    },
  },
  open: {
    height: 'auto',
    opacity: 1,
    marginTop: 8,
    transition: {
      duration: 0.3,
      ease: cubicEasingFn,
    },
  },
} satisfies Variants;

type DialogContent = { type: 'delete'; item: ChatHistoryItem } | null;

export function Menu({
  open,
  onClose,
  projects = [],
  type = 'overlay',
  className = '',
  paymentUrl = '',
}: {
  open: boolean;
  onClose?: () => void;
  projects?: ChatHistoryItem[];
  type?: 'overlay' | 'static';
  className?: string;
  paymentUrl?: string;
}) {
  const { t } = useTranslation();
  const menuRef = useRef<HTMLDivElement>(null);
  const [list, setList] = useState<ChatHistoryItem[]>([]);
  const [dialogContent, setDialogContent] = useState<DialogContent>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [collapsedCategories, setCollapsedCategories] = useState<Set<string>>(new Set());
  const [isProUpgradeModalOpen, setIsProUpgradeModalOpen] = useState(false);

  const shortcuts = useStore(shortcutsStore);
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    return shortcutEventEmitter.on('sidebarSearch', () => {
      searchInputRef.current?.focus();
    });
  }, []);

  const toggleCategory = (category: string) => {
    setCollapsedCategories((prev) => {
      const newSet = new Set(prev);

      if (newSet.has(category)) {
        newSet.delete(category);
      } else {
        newSet.add(category);
      }

      return newSet;
    });
  };

  // 'expanded' | 'collapsed' | 'mobile'
  // mobile = completely hidden (width 0), collapsed = narrow icon strip (desktop only)
  const [sidebarState, setSidebarState] = useState<'expanded' | 'collapsed' | 'mobile'>(
    isMobile() ? 'mobile' : 'expanded',
  );

  const isCollapsed = sidebarState === 'collapsed';
  const isMobileHidden = sidebarState === 'mobile';

  const setIsCollapsed = (val: boolean) => {
    if (val) {
      setSidebarState(isMobile() ? 'mobile' : 'collapsed');
    } else {
      setSidebarState('expanded');
    }
  };

  const chatStarted = useStore(chatStore).started;

  useEffect(() => {
    if (chatStarted || isMobile()) {
      setSidebarState(isMobile() ? 'mobile' : 'collapsed');
    }
  }, [chatStarted]);

  // Listen for 'expand-sidebar' custom event from the header
  useEffect(() => {
    const handleExpandSidebar = () => {
      setSidebarState('expanded');
    };

    window.addEventListener('expand-sidebar', handleExpandSidebar);

    return () => {
      window.removeEventListener('expand-sidebar', handleExpandSidebar);
    };
  }, []);

  const loadEntries = useCallback(async () => {
    if (projects && projects.length > 0) {
      const visibleProjects = projects.filter((project) => {
        if (!project.projectId || !project.description) {
          return false;
        }

        return true;
      });

      console.log('[Menu] menu visibleProjects:', visibleProjects);

      setList(visibleProjects);
    } else if (db) {
      console.log('[Menu] Loading projects from IndexedDB');
    }
  }, [projects]);

  const deleteItem = useCallback(
    (event: React.UIEvent, item: ChatHistoryItem) => {
      event.preventDefault();

      if (db) {
        if (item.projectId) {
          deleteByProjectId(db, item.projectId)
            .then(() => {
              loadEntries();

              if (projectId.get() === item.projectId) {
                // hard page navigation to clear the stores
                window.location.pathname = '/';
              }
            })
            .catch((error) => {
              Sentry.captureException(error, {
                extra: {
                  message: 'Error deleting chat history',
                  item,
                },
              });
              toast.error('Failed to delete conversation');
              logger.error(error);
            });
        }
      } else {
        // If using server projects, we need to implement server-side deletion
        console.warn('[Menu] Server project deletion not yet implemented');
        toast.error('Project deletion not yet implemented for server projects');
      }
    },
    [loadEntries],
  );

  const closeDialog = () => {
    setDialogContent(null);
  };

  const handleLoading = useCallback(() => {
    setIsLoading(true);
  }, []);

  const handleLogoClick = useCallback(() => {
    projectId.set(undefined);
    description.set(undefined);
    clearProjectRules();
  }, []);

  useEffect(() => {
    if (open) {
      loadEntries();
    }
  }, [open, loadEntries]);

  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  const isStatic = type === 'static';

  return (
    <>
      {/* Full-screen loader overlay */}
      {isLoading && <Loading heading="Loading your Project" screen={0} />}

      {/* Mobile backdrop overlay when sidebar is expanded */}
      {isStatic && !isMobileHidden && !isCollapsed && isMobile() && (
        <div className="fixed inset-0 bg-black/50 z-[39] md:hidden" onClick={() => setIsCollapsed(true)} />
      )}

      <motion.div
        ref={menuRef}
        initial={isStatic ? false : 'closed'}
        animate={isStatic ? undefined : open ? 'open' : 'closed'}
        variants={isStatic ? undefined : menuVariants}
        className={
          (isStatic
            ? `flex flex-col ${isMobileHidden ? 'w-0 border-0' : isCollapsed ? 'w-[68px]' : isMobile() ? 'fixed inset-y-0 left-0 w-full z-[40]' : 'w-[280px]'} h-full bg-dualite-background-primary ${isMobileHidden ? '' : 'border-r border-dualite-border-primary'} text-sm relative z-sidebar transition-all duration-300 overflow-hidden ${isCollapsed ? 'cursor-pointer' : ''}`
            : 'flex flex-col side-menu fixed top-0 left-0 w-full h-full m-0 rounded-none md:top-[8vh] md:w-[20vw] md:ml-4 md:mt-1 md:h-90vh md:rounded-xl bg-dualite-background-primary p-4 border border-dualite-border-primary z-sidebar shadow-xl shadow-alpha-elements-sidebar-dropdownShadow text-sm overflow-hidden') +
          (className ? ` ${className}` : '')
        }
        onClick={() => {
          if (isCollapsed) {
            setIsCollapsed(false);
          }
        }}
      >
        {isMobileHidden && isStatic ? null : isCollapsed && isStatic ? (
          <div className="flex flex-col items-center py-6 gap-6 h-full">
            <a
              href="/home"
              style={{ background: 'linear-gradient(180deg, #131314 0%, #1D1D1F 100%)' }}
              className="w-10 h-10 rounded-xl flex items-center justify-center border border-dualite-border-primary shadow-lg transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                handleLogoClick();
              }}
            >
              <img src="/images/dualite-logo.svg" alt="Dualite" className="w-6 h-6 brightness-0 invert" />
            </a>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setIsCollapsed(false);
              }}
              className="p-2 bg-dualite-background-button rounded-lg text-alpha-elements-textTertiary hover:bg-dualite-background-buttonHover hover:text-dualite-text-primary transition-colors"
            >
              <PanelLeftOpen size={20} className="text-dualite-text-primary" />
            </button>
            <a
              href="/home"
              onClick={(e) => {
                e.stopPropagation();
                handleLogoClick();
              }}
              className="p-2 bg-dualite-background-button rounded-lg text-alpha-elements-textTertiary hover:bg-dualite-background-buttonHover hover:text-dualite-text-primary transition-colors"
            >
              <SquarePen size={20} className="text-dualite-text-primary" />
            </a>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setIsCollapsed(false);
              }}
              className="p-2 bg-dualite-background-button rounded-lg text-alpha-elements-textTertiary hover:bg-dualite-background-buttonHover hover:text-dualite-text-primary transition-colors"
            >
              <Search size={20} className="text-dualite-text-primary" />
            </button>

            <div className="mt-auto pb-4">
              <UserStatus isCollapsed={true} onOpenUpgrade={() => setIsProUpgradeModalOpen(true)} />
            </div>
          </div>
        ) : (
          <div className="flex-1 flex flex-col h-full w-full min-w-[280px] overflow-hidden">
            <div className="bg-dualite-background-primary pt-6 px-4">
              <div className="flex items-center justify-between mb-8">
                <div className="flex items-center gap-3">
                  <a
                    href="/home"
                    style={{ background: 'linear-gradient(180deg, #131314 0%, #1D1D1F 100%)' }}
                    className="w-10 h-10 rounded-xl flex items-center justify-center border border-dualite-border-primary shadow-lg transition-colors"
                    onClick={handleLogoClick}
                  >
                    <img src="/images/dualite-logo.svg" alt="Dualite" className="w-6 h-6 brightness-0 invert" />
                  </a>
                  <span className="text-xl font-bold text-dualite-text-primary">Dualite</span>
                </div>
                <div className="flex items-center gap-2">
                  {onClose && (
                    <button
                      onClick={onClose}
                      className="md:hidden flex items-center justify-center text-alpha-elements-textTertiary hover:text-dualite-text-primary transition-colors"
                      aria-label={t('menu.closeMenu')}
                    >
                      <X size={20} />
                    </button>
                  )}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsCollapsed(true);
                    }}
                    className="block p-2 text-dualite-text-secondary hover:text-dualite-text-primary transition-colors"
                  >
                    <PanelLeftClose size={20} />
                  </button>
                </div>
              </div>

              <div className="space-y-2 mb-6">
                <a
                  href="/home"
                  onClick={handleLogoClick}
                  className="flex items-center justify-between w-full px-4 py-2.5 bg-dualite-background-tertiary hover:bg-dualite-background-buttonHover rounded-xl transition-all border border-dualite-border-primary group"
                >
                  <span className="text-dualite-text-primary font-medium">New Chat</span>
                  <div className="flex gap-1 items-center">
                    <kbd className="min-w-[20px] h-5 px-1 flex items-center justify-center rounded text-[12px] text-dualite-text-secondary border border-dualite-border-secondary hover:border-dualite-border-primary font-sans">
                      {/* <kbd className="min-w-[20px] h-5 px-1 flex items-center justify-center bg-[#2A2A2A] rounded text-[10px] text-alpha-elements-textTertiary border border-[#333333] font-sans">
                      ⌘
                    </kbd> */}
                      {/* <kbd className="min-w-[20px] h-5 px-1 flex items-center justify-center bg-[#2A2A2A] rounded text-[10px] text-alpha-elements-textTertiary border border-[#333333] font-sans">
                      {shortcuts.newChat?.key.toUpperCase()}
                    </kbd> */}
                      +
                    </kbd>
                  </div>
                </a>

                <div className="relative group">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2 text-alpha-elements-textTertiary group-focus-within:text-dualite-text-primary transition-colors pointer-events-none">
                    <Search size={18} className="text-dualite-text-primary" />
                  </div>
                  <input
                    ref={searchInputRef}
                    type="text"
                    placeholder="Search"
                    className="w-full pl-11 pr-16 py-2.5 bg-transparent hover:bg-dualite-background-button focus:bg-dualite-background-button rounded-xl text-dualite-text-primary placeholder:text-dualite-text-primary focus:outline-none border border-transparent focus:border-dualite-border-primary transition-all"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  <div className="absolute right-4 top-1/2 -translate-y-1/2 flex gap-1 items-center pointer-events-none">
                    <kbd className="min-w-[20px] h-5 px-1 flex items-center justify-center rounded text-[10px] text-dualite-text-secondary border border-dualite-border-secondary font-sans">
                      {isMac() ? '⌘' : 'Ctrl'}
                    </kbd>
                    <kbd className="min-w-[20px] h-5 px-1 flex items-center justify-center rounded text-[10px] text-dualite-text-secondary border border-dualite-border-secondary font-sans">
                      {shortcuts.sidebarSearch?.key.toUpperCase()}
                    </kbd>
                  </div>
                </div>
              </div>
            </div>

            <div
              className="flex-1 overflow-y-auto min-h-0 pb-5 px-4"
              style={{
                scrollbarWidth: 'thin',
                scrollbarColor: 'var(--dualite-background-tertiary) var(--dualite-background-primary)',
              }}
            >
              {list.length === 0 && (
                <div className="pl-2 text-dualite-text-secondary text-sm">{t('menu.noConversations')}</div>
              )}
              <DialogRoot open={dialogContent !== null}>
                {binDates(list).map(({ category, items }) => {
                  const isCollapsed = collapsedCategories.has(category);
                  const filteredItems = items.filter((item) =>
                    item.description?.toLowerCase().includes(debouncedSearchQuery.toLowerCase()),
                  );

                  if (filteredItems.length === 0) {
                    return null;
                  }

                  return (
                    <div key={category} className="mt-4 first:mt-0">
                      <button
                        onClick={() => toggleCategory(category)}
                        className="flex items-center w-full text-dualite-text-primary sticky top-0 z-1 pl-2 pt-2 pb-2 text-[13px] bg-dualite-background-primary group transition-colors rounded-md gap-2"
                      >
                        <span className="font-medium shrink-0">{category}</span>
                        <div className="h-[1px] flex-1 bg-dualite-border-primary" />
                        <div className="p-1 rounded bg-dualite-background-button border border-dualite-border-primary text-alpha-elements-textTertiary group-hover:text-dualite-text-primary hover:bg-dualite-background-buttonHover transition-colors shrink-0">
                          <motion.div
                            animate={{ rotate: isCollapsed ? 0 : 90 }}
                            transition={{ duration: 0.2, ease: cubicEasingFn }}
                            className="flex items-center justify-center"
                          >
                            <ChevronRight size={14} />
                          </motion.div>
                        </div>
                      </button>
                      <AnimatePresence initial={false}>
                        {!isCollapsed && (
                          <motion.div
                            initial="closed"
                            animate="open"
                            exit="closed"
                            variants={categoryVariants}
                            className="overflow-hidden"
                          >
                            <div className="space-y-2">
                              {filteredItems.map((item) => (
                                <HistoryItem
                                  key={item.projectId}
                                  item={item}
                                  onDelete={() => setDialogContent({ type: 'delete', item })}
                                  onLoading={handleLoading}
                                />
                              ))}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  );
                })}
                <Dialog onBackdrop={closeDialog} onClose={closeDialog}>
                  {dialogContent?.type === 'delete' && (
                    <>
                      <DialogTitle>Delete Chat?</DialogTitle>
                      <DialogDescription asChild>
                        <div>
                          <p>
                            You are about to delete <strong>{dialogContent.item.description}</strong>.
                          </p>
                          <p className="mt-1">Are you sure you want to delete this chat?</p>
                        </div>
                      </DialogDescription>
                      <div className="px-5 pb-4 bg-dualite-background-tertiary flex gap-2 justify-end">
                        <DialogButton type="secondary" onClick={closeDialog}>
                          Cancel
                        </DialogButton>
                        <DialogButton
                          type="danger"
                          onClick={(event) => {
                            deleteItem(event, dialogContent.item);
                            closeDialog();
                          }}
                        >
                          Delete
                        </DialogButton>
                      </div>
                    </>
                  )}
                </Dialog>
              </DialogRoot>
            </div>
            <div className="flex flex-col border-t border-dualite-border-primary bg-dualite-background-primary z-10 p-4">
              <UserStatus onOpenUpgrade={() => setIsProUpgradeModalOpen(true)} />
            </div>
          </div>
        )}
      </motion.div>

      <ProUpgradeModal
        isOpen={isProUpgradeModalOpen}
        onClose={() => setIsProUpgradeModalOpen(false)}
        isTopUp={planNameCleaner(useStore(userStore).planName).planName === 'Pro'}
        paymentUrl={paymentUrl}
      />
    </>
  );
}

function UserStatus({ isCollapsed = false, onOpenUpgrade }: { isCollapsed?: boolean; onOpenUpgrade: () => void }) {
  const userData = useStore(userStore);

  if (!userData.email) {
    return <div></div>;
  }

  const { planName: cleanedPlanName } = planNameCleaner(userData.planName);
  const isUnlimited = cleanedPlanName.includes('Launch') || cleanedPlanName.includes('Custom');
  const showProgressBar = !isUnlimited;

  if (isCollapsed) {
    return (
      <div className="flex justify-center w-full" onClick={onOpenUpgrade}>
        {userData.image ? (
          <img
            src={
              isDev
                ? 'https://proxy-worker.old-credit-270e.workers.dev/image?url=' + userData.image
                : userData.image || ''
            }
            alt="User Avatar"
            className="w-10 h-10 rounded-full object-cover shrink-0 border border-dualite-border-primary"
          />
        ) : (
          <Avatar src={userData.email} />
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col w-full gap-3">
      {showProgressBar && (
        <div className="bg-dualite-background-secondary border border-dualite-border-primary rounded-2xl p-4">
          <RemainingPrompt
            maxPrompts={userData.maxPrompts}
            promptsUsed={(parseInt(userData.promptsUsed) + parseInt(userData.topUpPromptsUsed)).toString()}
            email={userData.email}
            isUnlimited={isUnlimited}
            topUpPrompts={userData.topUpPrompts}
            planName={cleanedPlanName}
            onOpenUpgrade={onOpenUpgrade}
          />
        </div>
      )}
      <div className="flex items-center justify-between w-full">
        <div className="flex items-center gap-3 min-w-0">
          {userData.image ? (
            <img
              src={
                isDev
                  ? 'https://proxy-worker.old-credit-270e.workers.dev/image?url=' + userData.image
                  : userData.image || ''
              }
              alt="User Avatar"
              className="w-10 h-10 rounded-full object-cover shrink-0"
            />
          ) : (
            <Avatar src={userData.email} />
          )}
          <div className="flex flex-col min-w-0">
            <span className="text-dualite-text-primary font-semibold truncate text-xs leading-tight pb-0.5">
              {userData.email
                ? userData.email?.length > 25
                  ? userData.email?.slice(0, 25) + '...'
                  : userData.email
                : 'User'}
            </span>
            <span className="text-dualite-text-secondary text-xs font-medium">{cleanedPlanName} Plan</span>
          </div>
        </div>
        <button
          onClick={() => {
            window.location.href = '/settings';
          }}
          className="text-dualite-text-secondary hover:text-dualite-text-primary transition-colors p-2 rounded-xl bg-dualite-background-button border border-dualite-border-primary hover:bg-dualite-background-button-hover"
          title="Settings"
        >
          <div className="i-ph:gear-bold text-lg" />
        </button>
      </div>
    </div>
  );
}

function Avatar({ src }: { src: string }) {
  const avatar = src.split('@')[0].charAt(0).toUpperCase();
  return (
    <div className="w-10 h-10 flex items-center justify-center bg-dualite-background-tertiary rounded-full p-2 border border-dualite-border-primary">
      <span className="text-sm text-dualite-text-secondary">{avatar}</span>
    </div>
  );
}

function RemainingPrompt({
  maxPrompts,
  promptsUsed,
  email,
  isUnlimited,
  topUpPrompts,
  planName,
  onOpenUpgrade,
}: {
  maxPrompts: string;
  promptsUsed: string;
  email: string;
  isUnlimited: boolean;
  topUpPrompts?: string;
  planName: string;
  onOpenUpgrade: () => void;
}) {
  const used = parseInt(promptsUsed);
  const total = parseInt(maxPrompts) + parseInt(topUpPrompts || '0');
  const percentage = isUnlimited ? 0 : Math.min((used / total) * 100, 100);

  const getBarColor = () => {
    if (percentage >= 90) {
      return 'bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.4)]';
    }

    if (percentage >= 75) {
      return 'bg-yellow-400 shadow-[0_0_8px_rgba(250,204,21,0.4)]';
    }

    return 'bg-green-400 shadow-[0_0_8px_rgba(74,222,128,0.4)]';
  };

  return (
    <div className="flex flex-col gap-2 ">
      <div className="text-dualite-text-primary text-md font-bold tracking-tight">
        {isUnlimited
          ? `${promptsUsed} / Unlimited`
          : Number(topUpPrompts) > 0
            ? `${promptsUsed} / ${maxPrompts} + ${topUpPrompts}`
            : `${promptsUsed} / ${maxPrompts}`}{' '}
        <span className="text-dualite-text-secondary text-xs font-medium ml-1">messages</span>
      </div>
      {!isUnlimited && (
        <>
          <div className="h-2 w-full bg-dualite-background-button rounded-full overflow-hidden">
            <div
              className={`h-full ${getBarColor()} rounded-full transition-all duration-500`}
              style={{ width: `${percentage}%` }}
            />
          </div>
          {planName.toLowerCase() === 'free' && (
            <button
              onClick={onOpenUpgrade}
              className="mt-2 w-full py-3 px-3 bg-dualite-background-cta text-dualite-text-cta text-xs font-bold rounded-lg hover:bg-dualite-background-cta-hover transition-colors"
            >
              Upgrade
            </button>
          )}
          {planName.includes('Pro') && (
            <button
              onClick={onOpenUpgrade}
              className="mt-2 w-full py-3 px-3 bg-dualite-background-cta text-dualite-text-cta text-xs font-bold rounded-lg hover:bg-dualite-background-cta-hover transition-colors"
            >
              Get more messages
            </button>
          )}
        </>
      )}
    </div>
  );
}
